import { KeyRound, Mail, Smartphone } from "lucide-react";

import type { SettingsTranslations } from "@/shared/lang/interfaces/settings";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

interface GuideRow {
  icon: typeof Mail;
  label: keyof SettingsTranslations;
  text: keyof SettingsTranslations;
  tone: string;
  delayClass: string;
}

const ROWS: GuideRow[] = [
  {
    icon: Smartphone,
    label: "security_guide_totp_label",
    text: "security_guide_totp",
    tone: "border-success/30 bg-success/10 text-success",
    delayClass: "animate-delay-75",
  },
  {
    icon: KeyRound,
    label: "security_guide_pin_label",
    text: "security_guide_pin",
    tone: "border-primary/30 bg-primary/10 text-primary",
    delayClass: "animate-delay-150",
  },
  {
    icon: Mail,
    label: "security_guide_email_label",
    text: "security_guide_email",
    tone: "border-border bg-muted text-muted-foreground",
    delayClass: "animate-delay-200",
  },
];

/**
 * "Which one do I choose?" in three lines, under the two method cards: the app is the most secure, the PIN
 * the fastest, and the email is neither — it is the plan B of whichever one is chosen, which is why it is
 * drawn quieter than the other two.
 */
export function StepUpMethodGuide({ className }: { className?: string }) {
  const { t } = useTranslations("settings");
  const { t: tg } = useTranslations("global");

  return (
    <div className={cn("rounded-md border border-border bg-muted/30 p-3.5", className)}>
      <div className="mb-2.5 text-[12.5px] font-semibold text-foreground">{t("security_guide_title")}</div>
      <ul className="flex flex-col gap-2">
        {ROWS.map(({ icon: Icon, label, text, tone, delayClass }) => (
          <li
            key={label}
            className={cn(
              "flex animate-fade-up items-start gap-2.5 animate-duration-300 motion-reduce:animate-none",
              delayClass,
            )}
          >
            <span className={cn("grid h-6 w-6 shrink-0 place-items-center rounded-md border", tone)}>
              <Icon size={13} aria-hidden="true" />
            </span>
            <p className="min-w-0 pt-0.5 text-[12px] leading-snug text-muted-foreground">
              <span className="font-semibold text-foreground">{t(label)}</span> · {t(text)}
            </p>
          </li>
        ))}
      </ul>
      <p className="mt-2.5 border-t border-dashed border-border pt-2.5 text-[11.5px] text-muted-foreground">
        {tg("step_up_choice_footnote")}
      </p>
    </div>
  );
}
